import React, { useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { QRCode } from 'react-qrcode-logo';
import { Event } from 'services/api';
import { Routes } from 'services/routes';
import { AddToCalendarButton } from 'components/app/AddToCalendarButton';
import { AddToCalendarPlusIcon } from 'components/icons';
import { CHECK_IN_COLORS } from './colors';
import { DashedDivider, EventBlock } from './EventBlock';
import { TicketHolder } from './types';

interface MyCodeTabProps {
  event: Event;
  receiptId: string;
  ticket: TicketHolder;
}

export const MyCodeTab: React.FC<MyCodeTabProps> = ({
  event,
  receiptId,
  ticket,
}) => {
  const [qrValue, setQrValue] = useState<string | null>(null);

  useEffect(() => {
    if (typeof window !== 'undefined' && receiptId) {
      setQrValue(
        `${window.location.origin}${Routes.viewTicket(receiptId)}`,
      );
    }
  }, [receiptId]);

  const name = [ticket.firstName, ticket.lastName].filter(Boolean).join(' ');
  const ticketType =
    ticket.ticketType ||
    event.ticketTypes?.find(t => t.id === ticket.typeId)?.label ||
    'General Ticket';

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        backgroundColor: CHECK_IN_COLORS.sheetBg,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '26px',
        pt: '28px',
        px: '20px',
        overflow: 'auto',
      }}
    >
      <Box sx={{ width: '100%' }}>
        <EventBlock event={event} />
        <DashedDivider />
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '16px',
            alignItems: 'center',
            pt: '14px',
            pb: '26px',
            width: '100%',
          }}
        >
          <Typography
            sx={{
              fontFamily: 'Poppins, sans-serif',
              fontWeight: 400,
              fontSize: '12px',
              lineHeight: '18px',
              color: CHECK_IN_COLORS.text,
              textAlign: 'center',
            }}
          >
            Show this code to the event organizer.
          </Typography>
          <Box
            sx={{
              width: 204,
              height: 204,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: CHECK_IN_COLORS.white,
            }}
          >
            {qrValue && <QRCode value={qrValue} size={204} quietZone={0} />}
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: '3px',
              alignItems: 'center',
              pb: '4px',
              width: '100%',
              textAlign: 'center',
              color: CHECK_IN_COLORS.text,
            }}
          >
            <Typography
              sx={{
                fontFamily: 'Poppins, sans-serif',
                fontWeight: 600,
                fontSize: '17px',
                lineHeight: '22px',
              }}
            >
              {name}
            </Typography>
            <Typography
              sx={{
                fontFamily: 'Poppins, sans-serif',
                fontWeight: 400,
                fontSize: '13px',
                lineHeight: '19px',
              }}
            >
              {ticket.email}
            </Typography>
            <Typography
              sx={{
                fontFamily: 'Poppins, sans-serif',
                fontWeight: 400,
                fontSize: '13px',
                lineHeight: '19px',
              }}
            >
              {ticket.ticketNumber || '—'}
            </Typography>
            <Typography
              sx={{
                fontFamily: 'Poppins, sans-serif',
                fontWeight: 400,
                fontSize: '13px',
                lineHeight: '19px',
              }}
            >
              {ticketType}
            </Typography>
          </Box>
        </Box>
      </Box>

      <AddToCalendarButton
        event={event}
        icon={AddToCalendarPlusIcon}
        iconStyle={{ fontSize: 20, color: CHECK_IN_COLORS.navy }}
        gap={9}
      >
        <Typography
          sx={{
            fontFamily: 'Poppins, sans-serif',
            fontWeight: 500,
            fontSize: '14px',
            lineHeight: '21px',
            color: CHECK_IN_COLORS.navy,
          }}
        >
          Add to Calendar
        </Typography>
      </AddToCalendarButton>
    </Box>
  );
};
